import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Calendar,
  Clock,
  Tag,
  Layers,
  CheckCircle,
  Quote,
  ExternalLink,
  ChevronRight,
} from "lucide-react";
import { CASE_STUDIES } from "../data/mock";

// Animated Counter Component
const AnimatedCounter = ({ value, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  const stringValue = String(value || "0");
  const match = stringValue.match(/^([\d.]+)/);
  const numericValue = match ? parseFloat(match[1]) : 0;
  const hasDecimal = numericValue % 1 !== 0;
  const hasPlus = stringValue.includes("+");
  const hasPercent = stringValue.includes("%");
  const hasSlash = stringValue.includes("/");

  const unitMatch = stringValue.match(/[KMB]/i);
  const unit = unitMatch ? unitMatch[0] : "";

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000;
    const steps = 60;
    const increment = numericValue / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= numericValue) {
        setCount(numericValue);
        clearInterval(timer);
      } else {
        setCount(
          hasDecimal ? parseFloat(current.toFixed(1)) : Math.floor(current)
        );
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [isVisible, numericValue, hasDecimal]);

  if (hasSlash) {
    return <span ref={ref}>{hasDecimal ? count.toFixed(1) : count}/5</span>;
  }

  return (
    <span ref={ref}>
      {hasDecimal ? count.toFixed(1) : count.toLocaleString()}
      {unit}
      {hasPlus ? "+" : ""}
      {hasPercent ? "%" : ""}
      {suffix}
    </span>
  );
};

const CaseStudyDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const caseStudy = CASE_STUDIES.find((cs) => cs.slug === slug);
  const relatedCaseStudies = CASE_STUDIES.filter(
    (cs) => cs.slug !== slug
  ).slice(0, 3);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!caseStudy) {
    return (
      <div className="min-h-screen pt-32 pb-16 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Case Study Not Found
          </h1>
          <p className="text-gray-600 mb-8">
            The case study you're looking for doesn't exist.
          </p>
          <Link
            to="/resources/case-studies"
            className="inline-flex items-center px-6 py-3 bg-[#b32126] text-white font-semibold rounded-lg hover:bg-[#8f1a1f] transition-colors"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Back to Case Studies
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 bg-gradient-to-br from-gray-900 via-gray-800 to-[#b32126] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src={caseStudy.heroImage || caseStudy.image}
            alt={caseStudy.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 to-gray-900/70" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Breadcrumb */}
          <nav className="flex items-center text-sm text-gray-400 mb-8">
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <Link
              to="/resources/case-studies"
              className="hover:text-white transition-colors"
            >
              Case Studies
            </Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <span className="text-white">{caseStudy.title}</span>
          </nav>

          <div className="max-w-4xl">
            {/* Tags */}
            <div className="flex flex-wrap gap-3 mb-6">
              <Link
                to={`/industries/${caseStudy.industrySlug}`}
                className="inline-flex items-center px-4 py-1.5 bg-[#b32126] text-white text-sm font-medium rounded-full hover:bg-[#8f1a1f] transition-colors"
              >
                <Tag className="h-4 w-4 mr-2" />
                {caseStudy.industry}
              </Link>
              <span className="inline-flex items-center px-4 py-1.5 bg-white/10 text-white text-sm font-medium rounded-full">
                <Clock className="h-4 w-4 mr-2" />
                {caseStudy.timeline}
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              {caseStudy.title}
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8">
              {caseStudy.tagline}
            </p>

            {/* Client & Services */}
            <div className="flex flex-wrap gap-6 text-gray-300">
              <div>
                <span className="text-gray-400 text-sm">Client Type</span>
                <p className="font-semibold">{caseStudy.clientType}</p>
              </div>
              <div>
                <span className="text-gray-400 text-sm">Services</span>
                <p className="font-semibold">{caseStudy.services.join(", ")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Statistics */}
      {/* <section className="py-12 bg-[#b32126]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {caseStudy.results.map((result, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                  <AnimatedCounter value={result.value} />
                </div>
                <div className="text-red-100 text-sm font-medium">
                  {result.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Project Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Challenge */}
            <div>
              <h2 className="text-sm font-bold text-[#b32126] uppercase tracking-wider mb-3">
                The Challenge
              </h2>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Understanding the Problem
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                {caseStudy.challenge}
              </p>
            </div>

            {/* Solution */}
            <div>
              <h2 className="text-sm font-bold text-[#b32126] uppercase tracking-wider mb-3">
                Our Solution
              </h2>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                The Approach We Took
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                {caseStudy.solution}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key Features Delivered
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The capabilities and features that made this project successful
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {caseStudy.features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow border border-gray-100"
              >
                <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle className="h-5 w-5 text-[#b32126]" />
                </div>
                <p className="text-gray-800 font-medium">{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Deep Dive */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Results & Impact
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Measurable outcomes that demonstrate the project's success
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {caseStudy.results.map((result, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow"
              >
                <div className="text-4xl md:text-5xl font-bold text-[#b32126] mb-3">
                  {result.value}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {result.label}
                </h3>
                <p className="text-gray-600">{result.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Technologies Used
            </h2>
            <p className="text-gray-400">
              The tech stack that powered this solution
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {caseStudy.technologies.map((tech, index) => (
              <span
                key={index}
                className="px-6 py-3 bg-white/10 text-white rounded-full font-medium hover:bg-white/20 transition-colors"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      {caseStudy.testimonial && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Quote className="h-16 w-16 text-[#b32126]/20 mx-auto mb-6" />
            <blockquote className="text-2xl md:text-3xl font-medium text-gray-900 mb-8 leading-relaxed">
              "{caseStudy.testimonial.quote}"
            </blockquote>
            <div>
              <p className="text-lg font-semibold text-gray-900">
                {caseStudy.testimonial.author}
              </p>
              <p className="text-gray-500">{caseStudy.testimonial.role}</p>
            </div>
          </div>
        </section>
      )}

      {/* Related Links */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Related Industry */}
            <Link
              to={`/industries/${caseStudy.industrySlug}`}
              className="group bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all border border-gray-100"
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-bold text-[#b32126] uppercase tracking-wider">
                    Related Industry
                  </span>
                  <h3 className="text-2xl font-bold text-gray-900 mt-2 group-hover:text-[#b32126] transition-colors">
                    {caseStudy.industry}
                  </h3>
                  <p className="text-gray-600 mt-2">
                    Explore more solutions in this industry
                  </p>
                </div>
                <ArrowRight className="h-8 w-8 text-gray-400 group-hover:text-[#b32126] group-hover:translate-x-2 transition-all" />
              </div>
            </Link>

            {/* Related Services */}
            <div className="bg-white p-8 rounded-2xl shadow-md border border-gray-100">
              <span className="text-sm font-bold text-[#b32126] uppercase tracking-wider">
                Services Used
              </span>
              <div className="mt-4 space-y-3">
                {caseStudy.services.map((service, index) => (
                  <Link
                    key={index}
                    to={`/what-we-do/${caseStudy.servicesSlugs[index]}`}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                  >
                    <span className="text-gray-900 font-medium group-hover:text-[#b32126]">
                      {service}
                    </span>
                    <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-[#b32126]" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Case Studies */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Related Case Studies
            </h2>
            <p className="text-lg text-gray-600">
              Explore more of our successful projects
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {relatedCaseStudies.map((study) => (
              <Link
                key={study.id}
                to={`/resources/case-studies/${study.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all border border-gray-100"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#b32126] text-white text-xs font-semibold px-3 py-1 rounded-full">
                      {study.industry}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#b32126] transition-colors">
                    {study.title}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2">
                    {study.tagline}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#b32126]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Got a Similar Project in Mind?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help bring your vision to life
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-[#b32126] font-semibold rounded-xl hover:bg-gray-100 transition-colors shadow-lg"
          >
            Start Your Project
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CaseStudyDetailPage;
